// frontend-web/src/pages/Admin/PracticeSetList.jsx
import React, { useEffect, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

export default function PracticeSetList() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [allSets, setAllSets] = useState([]);
  const [filteredSets, setFilteredSets] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      nav("/login");
      return;
    }
    loadData();
  }, [nav]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [classRes, subjectRes, setRes] = await Promise.all([
        api.get("/admin/classes"),
        api.get("/admin/subjects"),
        api.get("/admin/practice_set/list")
      ]);

      const classList = classRes.data || [];
      const subjectList = subjectRes.data || [];
      const setList = setRes.data || [];

      setClasses(classList);
      setSubjects(subjectList);
      setAllSets(setList);

      // Auto select first class
      if (classList.length > 0) {
        setSelectedClass(classList[0]);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        alert("Session expired!");
        localStorage.removeItem("token");
        nav("/login");
      } else {
        alert("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter subjects for selected class
  const filteredSubjects = selectedClass
    ? subjects.filter(s => s.class_id === selectedClass.id)
    : [];

  // Filter practice sets
  useEffect(() => {
    if (!selectedClass) {
      setFilteredSets([]);
      return;
    }
    let filtered = allSets.filter(ps => ps.class_id === selectedClass.id);
    if (selectedSubject) {
      filtered = filtered.filter(ps => ps.subject_id === selectedSubject.id);
    }
    setFilteredSets(filtered);
  }, [selectedClass, selectedSubject, allSets]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Practice Set?")) return;
    try {
      setDeletingId(id);
      await api.delete(`/admin/practice_set/${id}`);
      setAllSets(prev => prev.filter(s => s.id !== id));
      alert("Deleted successfully!");
    } catch (err) {
      alert("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", fontSize: 20 }}>
        Loading Practice Sets...
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "20px 40px", background: "white", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={() => nav("/admin/dashboard")}
            style={{ padding: "10px 16px", background: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer" }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>View Practice Sets</h1>
        </div>
      </div>

      <div style={{ padding: 40 }}>
        {/* Class Tabs */}
        <div style={{ marginBottom: 30 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600 }}>Select Class</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {classes.map(cls => (
              <button
                key={cls.id}
                onClick={() => {
                  setSelectedClass(cls);
                  setSelectedSubject(null);
                }}
                style={{
                  padding: "10px 20px",
                  borderRadius: 30,
                  border: "none",
                  background: selectedClass?.id === cls.id ? "#3b82f6" : "#e2e8f0",
                  color: selectedClass?.id === cls.id ? "white" : "#475569",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "0.2s"
                }}
              >
                {cls.name}
              </button>
            ))}
          </div>
        </div>

        {/* Subject Tabs */}
        {selectedClass && (
          <div style={{ marginBottom: 30 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600 }}>Select Subject</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {filteredSubjects.length === 0 ? (
                <p style={{ color: "#94a3b8" }}>No subjects in this class</p>
              ) : (
                filteredSubjects.map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => setSelectedSubject(selectedSubject?.id === sub.id ? null : sub)}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 30,
                      border: "none",
                      background: selectedSubject?.id === sub.id ? "#10b981" : "#f1f5f9",
                      color: selectedSubject?.id === sub.id ? "white" : "#1e293b",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "0.2s"
                    }}
                  >
                    {sub.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        {selectedClass && (
          <div style={{ marginBottom: 24, padding: "16px 20px", background: "#f0fdf4", borderRadius: 12 }}>
            <h2 style={{ margin: 0, color: "#166534", fontSize: 20, fontWeight: 600 }}>
              {selectedClass.name}
              {selectedSubject ? ` → ${selectedSubject.name}` : ""} 
              <span style={{ marginLeft: 12, fontSize: 16, color: "#065f46" }}>
                ({filteredSets.length} Practice Set{filteredSets.length !== 1 ? "s" : ""})
              </span>
            </h2>
          </div>
        )}

        {/* Table */}
        {filteredSets.length === 0 ? (
          <div style={{ textAlign: "center", padding: 80, background: "white", borderRadius: 16 }}>
            <p style={{ fontSize: 18, color: "#64748b" }}>
              {selectedClass ? "No Practice Sets found" : "Please select a class"}
            </p>
          </div>
        ) : (
          <div style={{ background: "white", borderRadius: 16, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: "#1e293b" }}>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>Practice Set Name</th>
                  <th style={th}>Number of Question</th>
                  <th style={th}>Time Duration in Minutes</th>
                  <th style={th}>Class</th>
                  <th style={th}>Subject</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSets.map((s, index) => (
                  <tr key={s.id} style={{ background: index % 2 === 0 ? "#f8fafc" : "white" }}>
                    <td style={td}>{index + 1}</td>
                    <td style={td}><strong>{s.name}</strong></td>
                    <td style={td}>{s.num_questions} Qs</td>
                    <td style={td}>{s.duration_minutes} min</td>
                    <td style={td}>
                      <span style={{ background: "#dbeafe", color: "#4338ca", padding: "4px 10px", borderRadius: 20 }}>
                        Class {s.class_id}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: 20 }}>
                        Subject {s.subject_id}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ background: "#d1fae5", color: "#065f46", padding: "6px 12px", borderRadius: 20 }}>
                        Active
                      </span>
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => nav(`/admin/practice-set/${s.id}/preview`)}
                          style={{ ...btn, background: "#22c55e" }}
                        >
                          Quiz
                        </button>
                        <button onClick={() => nav(`/admin/practice-set/${s.id}`)} style={{ ...btn, background: "#3b82f6" }}>
                          View
                        </button>
                        <button onClick={() => nav(`/admin/edit-practice-set/${s.id}`)} style={{ ...btn, background: "#f59e0b" }}>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(s.id)}
                          disabled={deletingId === s.id}
                          style={{ ...btn, background: deletingId === s.id ? "#94a3b8" : "#ef4444" }}
                        >
                          {deletingId === s.id ? "..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles – bilkul tumhare screenshot jaisa
const th = {
  padding: "18px 16px",
  textAlign: "left",
  color: "white",
  fontWeight: 600,
  fontSize: 14
};

const td = {
  padding: "18px 16px",
  fontSize: 14,
  color: "#1e293b"
};

const btn = {
  padding: "8px 14px",
  color: "white",
  border: "none",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer"
};